import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import type { GeoLocation } from '@/generated/minetwin/types';

/**
 * Ordered orientation steps keyed by `orientationId`. Joins
 * `visible_orientation_step_links` (orientation_id, step_id, position) with
 * `visible_orientation_steps` (step display metadata) so the Trainee Portal
 * can read "what's next" without re-sorting on every render.
 *
 * Used by Story 7e to compute `nextStepId` for the
 * `advance_assignment(assignmentId, nextStepId)` reducer call. Steps inside
 * each list are sorted by ascending `position`.
 *
 * The step camera state is now a `GeoLocation` (lat/lon/elevation) — the
 * old `Viewpoint` enum was replaced by static geo points in the
 * `orientation_step.viewpoint -> location` rename Samuel shipped 2026-06-04.
 */
export type OrderedStep = {
  orientationId: string;
  stepId: string;
  position: number;
  displayName: string;
  location: GeoLocation;
  durationMinutes: number;
};

export function useOrientationStepOrder(): {
  stepsByOrientation: Map<string, OrderedStep[]>;
  isReady: boolean;
} {
  const [links, linksReady] = useTable(
    'minetwin',
    minetwinTables.visible_orientation_step_links,
  );
  const [steps, stepsReady] = useTable(
    'minetwin',
    minetwinTables.visible_orientation_steps,
  );

  const stepsByOrientation = useMemo(() => {
    const stepById = new Map<string, (typeof steps)[number]>();
    for (const s of steps) stepById.set(s.stepId, s);

    const out = new Map<string, OrderedStep[]>();
    for (const link of links) {
      const s = stepById.get(link.stepId);
      if (!s) continue;
      const list = out.get(link.orientationId) ?? [];
      list.push({
        orientationId: link.orientationId,
        stepId: link.stepId,
        position: link.position,
        displayName: s.displayName,
        location: s.location,
        durationMinutes: s.durationMinutes,
      });
      out.set(link.orientationId, list);
    }
    for (const list of out.values()) list.sort((a, b) => a.position - b.position);
    return out;
  }, [links, steps]);

  return { stepsByOrientation, isReady: linksReady && stepsReady };
}
